/**
 * Snapshots list page component.
 *
 * @module pages/SnapshotsPage
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Camera, Clock, ChevronLeft, ChevronRight, Play, Trash2 } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';
import { LoadingPage, ConfirmModal } from '@/components/ui';
import {
  useSnapshots,
  useSnapshotsCount,
  useDeleteSnapshot,
  useSandboxGroupSnapshots,
  useDeleteSandboxGroupSnapshot,
  snapshotKeys,
  sandboxGroupKeys,
} from '@/hooks';
import { snapshotService, sandboxGroupService } from '@/services';
import type { SnapshotResponse } from '@/types';

/**
 * Snapshots list page
 */
export function SnapshotsPage() {
  const { groupId } = useParams<{ groupId?: string }>();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const { data: allSnapshots, isLoading: allLoading } = useSnapshots(page, pageSize);
  const { data: allTotal = 0 } = useSnapshotsCount();
  const { data: groupSnapshots, isLoading: groupLoading } = useSandboxGroupSnapshots(
    groupId ?? '',
    !!groupId
  );
  const snapshots = groupId ? groupSnapshots : allSnapshots;
  const total = groupId ? (groupSnapshots?.length ?? 0) : allTotal;
  const isLoading = groupId ? groupLoading : allLoading;
  const deleteMutation = useDeleteSnapshot();
  const deleteGroupSnapshotMutation = useDeleteSandboxGroupSnapshot();
  const queryClient = useQueryClient();

  const [deleteTarget, setDeleteTarget] = useState<SnapshotResponse | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState({
    current: 0,
    total: 0,
    currentId: '',
  });
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleDelete = () => {
    if (deleteTarget) {
      if (groupId) {
        deleteGroupSnapshotMutation.mutate(
          { groupId, snapshotId: deleteTarget.id },
          { onSuccess: () => setDeleteTarget(null) }
        );
      } else {
        deleteMutation.mutate(deleteTarget.id, {
          onSuccess: () => setDeleteTarget(null),
        });
      }
    }
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  };

  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setPage(1); // Reset to first page when changing page size
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked && snapshots) {
      setSelectedIds(new Set(snapshots.map((s) => s.id)));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedIds(newSelected);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;

    setIsBulkDeleting(true);
    const idsToDelete = Array.from(selectedIds);
    setBulkDeleteProgress({ current: 0, total: idsToDelete.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    // Process in batches of 5
    for (let i = 0; i < idsToDelete.length; i += BATCH_SIZE) {
      const batch = idsToDelete.slice(i, i + BATCH_SIZE);

      // Run batch in parallel
      await Promise.all(
        batch.map(async (id) => {
          try {
            if (groupId) {
              await sandboxGroupService.deleteSnapshot(groupId, id);
            } else {
              await snapshotService.delete(id);
            }
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete snapshot ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: idsToDelete.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    // Invalidate queries to refresh the list
    if (groupId) {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.snapshots(groupId) });
    } else {
      queryClient.invalidateQueries({ queryKey: snapshotKeys.all });
    }

    setBulkDeleteProgress({
      current: idsToDelete.length,
      total: idsToDelete.length,
      currentId: '',
    });
    setIsBulkDeleting(false);
    setSelectedIds(new Set());

    // Close modal after a brief delay to show completion
    setTimeout(() => {
      setShowBulkDeleteModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const handleDeleteAll = async () => {
    if (total === 0) return;

    setIsBulkDeleting(true);

    // Collect all IDs to delete
    const allIds: string[] = [];

    if (groupId) {
      // For group-scoped view, we already have all snapshots in memory
      allIds.push(...(snapshots ?? []).map((s) => s.id));
    } else {
      // Fetch all snapshot IDs using the service
      const totalPagesCount = Math.ceil(total / 100);
      try {
        for (let pageNum = 1; pageNum <= totalPagesCount; pageNum++) {
          const fetchedSnapshots = await snapshotService.list(pageNum, 100);
          allIds.push(...fetchedSnapshots.map((s) => s.id));
        }
      } catch (error) {
        console.error('Failed to fetch all snapshots:', error);
        setIsBulkDeleting(false);
        return;
      }
    }

    setBulkDeleteProgress({ current: 0, total: allIds.length, currentId: '' });

    const BATCH_SIZE = 5;
    let completed = 0;

    // Process in batches of 5
    for (let i = 0; i < allIds.length; i += BATCH_SIZE) {
      const batch = allIds.slice(i, i + BATCH_SIZE);

      // Run batch in parallel
      await Promise.all(
        batch.map(async (id) => {
          try {
            if (groupId) {
              await sandboxGroupService.deleteSnapshot(groupId, id);
            } else {
              await snapshotService.delete(id);
            }
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          } catch (error) {
            console.error(`Failed to delete snapshot ${id}:`, error);
            completed++;
            setBulkDeleteProgress({
              current: completed,
              total: allIds.length,
              currentId: batch.length > 1 ? `Batch ${Math.floor(i / BATCH_SIZE) + 1}` : id,
            });
          }
        })
      );
    }

    // Invalidate queries to refresh the list
    if (groupId) {
      queryClient.invalidateQueries({ queryKey: sandboxGroupKeys.snapshots(groupId) });
    } else {
      queryClient.invalidateQueries({ queryKey: snapshotKeys.all });
    }

    setBulkDeleteProgress({ current: allIds.length, total: allIds.length, currentId: '' });
    setIsBulkDeleting(false);

    // Close modal after a brief delay to show completion
    setTimeout(() => {
      setShowDeleteAllModal(false);
      setBulkDeleteProgress({ current: 0, total: 0, currentId: '' });
    }, 1000);
  };

  const isAllSelected =
    snapshots && snapshots.length > 0 && snapshots.every((s) => selectedIds.has(s.id));

  if (isLoading) {
    return <LoadingPage />;
  }

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Snapshots</h1>
          {groupId && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Group: <span className="font-medium text-gray-700 dark:text-gray-300">{groupId}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <a
            href="https://docs.example.com/onboarding"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Onboarding guide
          </a>
          <a
            href="https://docs.example.com"
            className="text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Docs
          </a>
        </div>
      </div>

      {/* Bulk actions */}
      {selectedIds.size > 0 && (
        <div className="mb-3 flex items-center gap-3 px-4 py-2 bg-gray-50 dark:bg-[#161616] border border-gray-200 dark:border-[#2a2a2a] rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {selectedIds.size} snapshot{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete Selected
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" aria-label="Snapshots">
            <thead className="bg-white dark:bg-[#0a0a0a] border-b border-gray-200 dark:border-[#2a2a2a]">
              <tr>
                <th scope="col" className="px-6 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                    aria-label="Select all snapshots"
                  />
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Snapshot ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Sandbox ID
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Resources
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Created
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#0a0a0a] divide-y divide-gray-200 dark:divide-[#2a2a2a]">
              {!snapshots || snapshots.length === 0 ? (
                <tr className="bg-white dark:bg-[#0a0a0a]">
                  <td colSpan={6} className="px-6 py-32">
                    <div className="flex flex-col items-center justify-center text-center">
                      <Camera className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-4" />
                      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-1">
                        No Snapshots yet.
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                        Snapshots will appear here once you create them from sandboxes.
                      </p>
                      <p className="text-sm text-gray-900 dark:text-white">
                        <span className="font-medium">Check out the Onboarding guide</span> to learn
                        more.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                snapshots.map((snapshot) => (
                  <tr key={snapshot.id} className="hover:bg-gray-50 dark:hover:bg-[#161616]">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(snapshot.id)}
                        onChange={(e) => handleSelectOne(snapshot.id, e.target.checked)}
                        className="rounded border-gray-300 dark:border-gray-700 dark:bg-[#161616]"
                        aria-label={`Select snapshot ${snapshot.id}`}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-200">
                        {snapshot.id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {snapshot.sandboxId || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {snapshot.resources ? (
                          <>
                            {snapshot.resources.cpu} CPU / {snapshot.resources.memory}
                            {snapshot.resources.disk && ` / ${snapshot.resources.disk}`}
                            {snapshot.resources.gpu &&
                              ` / ${snapshot.resources.gpu.sku} x${snapshot.resources.gpu.quantity}`}
                          </>
                        ) : (
                          '-'
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
                        <Clock className="h-3.5 w-3.5" />
                        {formatDistanceToNow(new Date(snapshot.createdAtUtc), { addSuffix: true })}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end items-center gap-2">
                        <Link
                          to={
                            groupId
                              ? `/sandbox-groups/${encodeURIComponent(groupId)}/sandboxes/new?snapshotId=${encodeURIComponent(snapshot.id)}&groupId=${encodeURIComponent(groupId)}`
                              : `/sandboxes/new?snapshotId=${encodeURIComponent(snapshot.id)}`
                          }
                        >
                          <button className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-success-700 dark:text-success-400 hover:bg-success-50 dark:hover:bg-success-900/20 rounded transition-colors">
                            <Play className="h-4 w-4" />
                            Create Sandbox
                          </button>
                        </Link>
                        <button
                          onClick={() => setDeleteTarget(snapshot)}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer — hidden for group-scoped views (all snapshots are returned at once) */}
        {!groupId && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-200 dark:border-[#2a2a2a] bg-white dark:bg-[#0a0a0a]">
            <div className="flex items-center gap-4">
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="text-sm border border-gray-300 dark:border-[#2a2a2a] dark:bg-[#161616] dark:text-white rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
                aria-label="Results per page"
              >
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Showing {snapshots && snapshots.length > 0 ? (page - 1) * pageSize + 1 : 0} to{' '}
                {Math.min(page * pageSize, total)} of {total} snapshots
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={handlePreviousPage}
                disabled={page <= 1}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={handleNextPage}
                disabled={page >= totalPages}
                className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-[#161616] disabled:opacity-50 disabled:cursor-not-allowed text-gray-600 dark:text-gray-400"
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete All Button */}
      {total > 0 && (
        <div className="mt-3">
          <button
            onClick={() => setShowDeleteAllModal(true)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            <Trash2 className="h-4 w-4" />
            Delete All ({total})
          </button>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Snapshot"
        message={`Are you sure you want to delete snapshot "${deleteTarget?.id}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending || deleteGroupSnapshotMutation.isPending}
      />

      {/* Bulk Delete Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="bulk-delete-snap-title"
          >
            <div className="p-6">
              <h2
                id="bulk-delete-snap-title"
                className="text-xl font-semibold text-gray-900 dark:text-white mb-4"
              >
                Delete {selectedIds.size} Snapshot{selectedIds.size > 1 ? 's' : ''}?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete {selectedIds.size} snapshot
                    {selectedIds.size > 1 ? 's' : ''}? This action cannot be undone.
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowBulkDeleteModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleBulkDelete}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete {selectedIds.size} Snapshot{selectedIds.size > 1 ? 's' : ''}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting snapshots...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#2a2a2a] rounded-full h-2"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Bulk delete progress"
                    >
                      <div
                        className="bg-red-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      />
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 truncate">
                        Processing: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All snapshots deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete All Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div
            className="bg-white dark:bg-[#0a0a0a] border border-gray-200 dark:border-[#2a2a2a] rounded-lg shadow-xl max-w-md w-full mx-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-all-snap-title"
          >
            <div className="p-6">
              <h2
                id="delete-all-snap-title"
                className="text-xl font-semibold text-red-600 dark:text-red-400 mb-4"
              >
                ⚠️ Delete All {total} Snapshots?
              </h2>

              {!isBulkDeleting ? (
                <>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    This will permanently delete <strong>all {total} snapshots</strong>.
                  </p>
                  <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-6">
                    This action cannot be undone!
                  </p>
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => setShowDeleteAllModal(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#161616] border border-gray-300 dark:border-[#2a2a2a] rounded-lg hover:bg-gray-50 dark:hover:bg-[#1a1a1a] transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteAll}
                      className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                    >
                      Delete All Snapshots
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2">
                      <span>Deleting all snapshots...</span>
                      <span>
                        {bulkDeleteProgress.current} / {bulkDeleteProgress.total}
                      </span>
                    </div>
                    <div
                      className="w-full bg-gray-200 dark:bg-[#2a2a2a] rounded-full h-2"
                      role="progressbar"
                      aria-valuenow={bulkDeleteProgress.current}
                      aria-valuemin={0}
                      aria-valuemax={bulkDeleteProgress.total}
                      aria-label="Delete all progress"
                    >
                      <div
                        className="bg-red-600 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${(bulkDeleteProgress.current / bulkDeleteProgress.total) * 100}%`,
                        }}
                      />
                    </div>
                    {bulkDeleteProgress.currentId && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 truncate">
                        Processing: {bulkDeleteProgress.currentId}
                      </p>
                    )}
                  </div>
                  {bulkDeleteProgress.current === bulkDeleteProgress.total && (
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                      ✓ All snapshots deleted successfully
                    </p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
